import { getDefaultTimezone } from 'app/project/selectors';
import { formatInTimeZone } from 'date-fns-tz';
import prettyMs from 'pretty-ms';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { getUserId } from '../auth/selectors';
import { getCurrentBranchPath } from '../branches/selectors';
import handleError from '../common/utils/handle-error';
import { createContent, updateContent } from '../content';
import { showMessage, showUserMessage, type UserMessageProps } from '../messages/actions';
import { getDefaultLocale, getProjectId } from '../schema';
import { SUBMIT_FORM } from '../schema-form/action-types';
import { type SubmitFormFailureAction, type SubmitFormSuccessAction, submitForm } from '../schema-form/actions';
import { waitForFormSubmit } from '../schema-form/saga';
import { getData, getValues } from '../schema-form/selectors';
import { getLatestDeploys, publishSite, saveSite } from './actions';
import { deploySite, getLatestSiteDeploysQuery, siteDeployStatusQuery, validateBucketCredentials } from './api';
import { FORM_NAME, SHAPE_ID } from './constants';

export const NOTIFICATION_DELAY = 1500;

export function* publishSiteSaga(action: ActionType<typeof publishSite.requested>): SagaIterator {
  const { meta } = action;
  try {
    const response = yield call(siteDeployStatusQuery, { siteId: meta.siteId });
    const { status } = response.site;
    if (status === 'running') {
      yield put(
        showMessage(
          'info',
          `Publishing to ${meta.siteTitle} is already in progress. Please wait to publish again.`,
          NOTIFICATION_DELAY
        )
      );
    } else {
      const projectId = yield select(getProjectId);
      yield call(deploySite, projectId, meta.siteId);
      const messageParams: UserMessageProps = {
        type: 'info',
        timeout: NOTIFICATION_DELAY,
        message: 'Started publishing...'
      };
      yield put(showUserMessage(messageParams));
    }
  } catch (error) {
    const errorAction = yield call(handleError, error);
    if (errorAction) {
      yield put(errorAction);
    }

    yield put(publishSite.rejected({ error: error.message }, meta));
  }
}

export function* publishSitePendingSaga(action: ActionType<typeof publishSite.pending>): SagaIterator {
  const { siteTitle } = action.meta;
  const { user, date } = action.payload;
  const userId = yield select(getUserId);
  const tz = yield select(getDefaultTimezone);
  const time = yield call(formatInTimeZone, date, tz, 'h:mm a');

  const messageParams: UserMessageProps = {
    type: 'info',
    timeout: NOTIFICATION_DELAY,
    message: `Started publishing to ${siteTitle} at ${time}`
  };
  if (user && user.id !== userId) {
    messageParams.user = user;
    yield put(showUserMessage(messageParams));
  }
}

export function* getLatestDeploysSaga(): SagaIterator {
  yield put(getLatestDeploys.pending());
  try {
    const response = yield call(getLatestSiteDeploysQuery);
    const { sites } = response;
    yield put(getLatestDeploys.fulfilled({ sites }));
  } catch (error) {
    yield put(getLatestDeploys.rejected({ message: error.message }));
  }
}

export function* publishSiteFulfilledSaga(action: ActionType<typeof publishSite.fulfilled>): SagaIterator {
  const { siteTitle } = action.meta;
  const { user, date, duration } = action.payload;
  const userId = yield select(getUserId);
  const tz = yield select(getDefaultTimezone);
  const time = yield call(formatInTimeZone, date, tz, 'h:mm a');
  const prettyDuration = prettyMs(duration);
  const isMe = user && user.id === userId;

  const messageParams: UserMessageProps = {
    type: 'info',
    timeout: NOTIFICATION_DELAY,
    message: `Published to ${siteTitle} successfully at ${time} and it took ${prettyDuration}`
  };
  if (!isMe) {
    messageParams.user = user;
  }

  yield put(showUserMessage(messageParams));
  if (isMe) {
    yield put(getLatestDeploys.requested());
  }
}

export function formatError(error: string): string {
  return !error.includes('\n') ? `\`${error}\`` : `\n\`\`\`\n${error}\n\`\`\``;
}

export function* publishSiteRejectedSaga(action: ActionType<typeof publishSite.rejected>): SagaIterator {
  const { siteTitle } = action.meta;
  const { error, userId } = action.payload;
  const currentUserId = yield select(getUserId);
  if (userId === currentUserId) {
    yield put(showMessage('error', `Failed to publish to ${siteTitle}: ${formatError(error ?? 'Unknown error')}`));
  }
}

export function* saveSiteSaga(): SagaIterator {
  yield put(saveSite.pending());

  const contentId = yield select(getData, {
    formName: FORM_NAME,
    path: ['_id']
  });
  const isNew = !contentId;

  yield put(submitForm({ formName: FORM_NAME, onlyDirty: !isNew, withValues: true }));

  const action: SubmitFormFailureAction | SubmitFormSuccessAction = yield call(waitForFormSubmit, FORM_NAME);
  if (action.type === `${SUBMIT_FORM}_FAILURE`) {
    yield put(saveSite.rejected());
    return;
  }

  if (!('payload' in action)) {
    return;
  }

  const { data, structuralChanges } = action.payload;

  const allValues = isNew ? data : yield select(getValues, { formName: FORM_NAME, onlyDirty: false });
  if (data.secretKey) {
    try {
      const payload = {
        provider: allValues.provider,
        destination: allValues.destination,
        idKey: allValues.idKey,
        secretKey: allValues.secretKey
      };
      const {
        data: { valid, message }
      } = yield call(validateBucketCredentials, payload);
      yield put(
        showMessage(valid ? 'info' : 'error', message || `${valid ? 'Valid' : 'Invalid'} Deployment Credentials`, 5000)
      );
      if (!valid) {
        yield put(saveSite.rejected());
        return;
      }
    } catch (error) {
      yield put(showMessage('error', error.message, 5000));
      yield put(saveSite.rejected());
      return;
    }
  }

  const locale = yield select(getDefaultLocale);
  const branchPath = yield select(getCurrentBranchPath);
  const params = {
    isSingleton: false,
    shapeId: SHAPE_ID,
    redirectOnSuccess: `${branchPath}/sites`,
    locale
  };

  if (isNew) {
    yield put(createContent.requested({ input: data, structure: [] }, { ...params, source: FORM_NAME }));
  } else {
    const input = { ...data, _version: allValues._version };
    yield put(updateContent.requested({ input, structure: structuralChanges }, { ...params, contentId }));
  }

  yield put(saveSite.fulfilled());
}

export default function* root(): SagaIterator {
  yield all([
    takeEvery(getLatestDeploys.REQUESTED, getLatestDeploysSaga),
    takeEvery(saveSite.REQUESTED, saveSiteSaga),
    takeEvery(publishSite.REQUESTED, publishSiteSaga),
    takeEvery(publishSite.PENDING, publishSitePendingSaga),
    takeEvery(publishSite.FULFILLED, publishSiteFulfilledSaga),
    takeEvery(publishSite.REJECTED, publishSiteRejectedSaga)
  ]);
}
